import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SolutionsSection from "./SolutionSection";
import SolutionGrid from "./SolutionsGrid";

export type SolutionCategory =
  | "BESS"
  | "EV Infrastructure"
  | "Home"
  | "Ships"
  | "Tools"
  | "Agriculture"
  | "Data Center"
  | "UAV";

export interface Solution {
  id: string;
  name: string;
  displayName: string;
  image: string;
  keyFeatures: {
    capacity?: string;
    output?: string;
    recharge?: string;
    efficiency?: string;
    lifespan?: string;
  };
  features: string[];
}

const SolutionsComponent: React.FC = () => {
  // Make selectedSolution nullable
  const [selectedSolution, setSelectedSolution] = useState<SolutionCategory | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const solutionSectionRef = useRef<HTMLDivElement>(null);
  const solutionGridRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const solutionsData: Record<SolutionCategory, Solution> = {
    BESS: {
      id: "bess",
      name: "BESS",
      displayName: "Battery Energy Storage System",
      image: "/solutions/bess.png",
      keyFeatures: {
        capacity: "1-100 MWh",
        output: "Grid-scale Power",
        recharge: "Solar/Wind/Grid",
        efficiency: "≥95%",
        lifespan: "15+ years",
      },
      features: [
        "Reduces peak demand charges by up to 40%",
        "Enables renewable energy integration and storage",
        "Provides backup power during grid outages",
        "Improves grid stability and frequency regulation",
        "Extends battery lifespan with advanced BMS",
        "Lowers carbon footprint and energy costs",
        "Scalable design for various capacity needs",
        "Remote monitoring and smart energy management",
      ],
    },
    "EV Infrastructure": {
      id: "ev-infrastructure",
      name: "EV Infrastructure",
      displayName: "EV Charging Infrastructure",
      image: "/solutions/evinfra.png",
      keyFeatures: {
        capacity: "50-350 kW",
        output: "DC Fast Charging",
        recharge: "30-45 minutes",
        efficiency: "92-96%",
        lifespan: "10+ years",
      },
      features: [
        "Fast charging capability reduces wait times",
        "Smart grid integration prevents overload",
        "Multiple connector types for all EV models",
        "Weather-resistant design for outdoor installation",
        "Remote monitoring and maintenance alerts",
        "Payment integration and user authentication",
        "Scalable network management system",
        "Future-proof technology for evolving standards",
      ],
    },
    Home: {
      id: "home",
      name: "Home",
      displayName: "Home Energy Solutions",
      image: "/solutions/homeenergy.jpg",
      keyFeatures: {
        capacity: "5-20 kWh",
        output: "Whole Home Backup",
        recharge: "Solar/Grid",
        efficiency: "90%+",
        lifespan: "10+ years",
      },
      features: [
        "Smart home integration and energy monitoring",
        "Solar and Grid Compatible",
        "Custom BMS",
        "Increases property value and energy independence",
        "Portable and Lightweight",
        "All Weather Resistant",
        "Increased Energy Density",
        "Remote control via mobile app",
      ],
    },
    Ships: {
      id: "ships",
      name: "Ships",
      displayName: "Marine Power Solutions",
      image: "/solutions/ships.png",
      keyFeatures: {
        capacity: "100-5000 kWh",
        output: "Marine Grade Power",
        recharge: "Shore/Generator",
        efficiency: "94%+",
        lifespan: "8-12 years",
      },
      features: [
        "Reduces fuel consumption and emissions",
        "Provides silent operation at port",
        "Enables hybrid propulsion systems",
        "Withstands harsh marine environments",
        "Compliant with international marine standards",
        "Reduces maintenance and operating costs",
        "Enhances vessel maneuverability and performance",
        "Backup power for critical navigation systems",
      ],
    },
    Tools: {
      id: "tools",
      name: "Tools",
      displayName: "Industrial Tools Power",
      image: "/solutions/toolssolution.webp",
      keyFeatures: {
        capacity: "1-10 kWh",
        output: "Industrial Grade",
        recharge: "1-2 hours",
        efficiency: "88%+",
        lifespan: "3-5 years",
      },
      features: [
        "Cordless operation increases worksite safety",
        "Extended runtime for continuous operation",
        "Fast charging minimizes downtime",
        "Lightweight and portable designs",
        "Compatible with existing tool ecosystems",
        "High power output for demanding applications",
        "Durable construction for tough environments",
        "Reduces noise pollution on worksites",
      ],
    },
    Agriculture: {
      id: "agriculture",
      name: "Agriculture",
      displayName: "Agricultural Power Solutions",
      image: "/solutions/agriculture.png",
      keyFeatures: {
        capacity: "10-100 kWh",
        output: "Farm Equipment Ready",
        recharge: "Solar/Diesel",
        efficiency: "92%+",
        lifespan: "8-10 years",
      },
      features: [
        "Powers electric tractors and farm equipment",
        "Enables solar-powered irrigation systems",
        "Reduces diesel consumption and emissions",
        "Provides reliable power in remote locations",
        "Low maintenance compared to diesel generators",
        "Supports precision agriculture technology",
        "Weather-resistant for outdoor farm use",
        "Improves farm sustainability and efficiency",
      ],
    },
    "Data Center": {
      id: "Data Center",
      name: "Data Center",
      displayName: "Data Center Power Solutions",
      image: "/solutions/defense.png",
      keyFeatures: {
        capacity: "100-1000 MWh",
        output: "Mission Critical",
        recharge: "Grid/Renewables",
        efficiency: "96%+",
        lifespan: "15+ years",
      },
      features: [
        "Provides uninterrupted power supply for critical servers",
        "Enables renewable energy integration for green Data Centers",
        "Reduces peak power consumption costs",
        "Supports load shifting and demand response programs",
        "Modular design for scalable deployment",
        "Advanced thermal management for optimal battery performance",
        "Real-time monitoring and predictive maintenance",
        "Compliant with Data Center reliability standards (Tier III/IV)",
        "Backup power for extended grid outages",
        "Integration with existing UPS systems",
      ],
    },
    UAV: {
      id: "uav",
      name: "UAV",
      displayName: "Drone Battery Systems",
      image: "/solutions/uav.png",
      keyFeatures: {
        capacity: "0.5-5 kWh",
        output: "UAV Optimized",
        recharge: "30-60 minutes",
        efficiency: "85%+",
        lifespan: "500+ cycles",
      },
      features: [
        "Extended flight time up to 60+ minutes",
        "High energy density for longer missions",
        "Fast charging enables rapid turnaround",
        "Lightweight design maximizes payload capacity",
        "Advanced thermal management for all climates",
        "Safe operation with multiple protection systems",
        "Compatible with various drone models and sizes",
        "Real-time battery monitoring and diagnostics",
      ],
    },
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const solutionParam = searchParams.get("solution");

    if (solutionParam) {
      const decodedSolution = decodeURIComponent(solutionParam);
      const solutionMap: Record<string, SolutionCategory> = {
        BESS: "BESS",
        "EV Infrastructure": "EV Infrastructure",
        Home: "Home",
        Ships: "Ships",
        Tools: "Tools",
        Agriculture: "Agriculture",
        "Data Center": "Data Center",
        UAV: "UAV",
      };

      const category = solutionMap[decodedSolution];
      if (category && category !== selectedSolution) {
        setSelectedSolution(category);
        setTimeout(() => {
          scrollToSolution();
        }, 100);
      }
    }
  }, [location.search]);

  const handleSolutionChange = (solution: SolutionCategory) => {
    setIsScrolling(true);
    setSelectedSolution(solution);

    navigate(`/solutions?solution=${encodeURIComponent(solution)}`, {
      replace: true,
    });

    setTimeout(() => {
      scrollToSolution();
      setIsScrolling(false);
    }, 100);
  };

  const scrollToSolution = () => {
    if (solutionSectionRef.current) {
      const offset = 100;
      const elementPosition =
        solutionSectionRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Get current solution or undefined if none selected
  const currentSolution = selectedSolution ? solutionsData[selectedSolution] : undefined;

  return (
    <div className="w-full max-w-8xl py-2" ref={solutionSectionRef}>
      <div className="p-4">
        <div className="rounded-xl border mb-2" ref={solutionGridRef}>
          <SolutionGrid
            selectedSolution={selectedSolution || "Home"}
            onSolutionChange={handleSolutionChange}
            isScrolling={isScrolling}
          />
        </div>
      </div>

      {/* Only show SolutionsSection when a solution is selected */}
      {currentSolution && (
        <SolutionsSection
          solution={currentSolution}
          isActive={!isScrolling}
          id={selectedSolution!.toLowerCase().replace(/\s+/g, "-")}
        />
      )}
    </div>
  );
};

export default SolutionsComponent;